import React, { useState, useEffect } from'react';
import {
 AreaChart as RechartsAreaChart,
 Area,
 XAxis,
 YAxis,
 CartesianGrid,
 Tooltip,
 ResponsiveContainer,
 Legend,
} from'recharts';
import ExportButton from'../ExportButton';

const COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4'];

/**
 * Compact tick formatter for count axes: 1500 -> "1.5k", 2000000 -> "2M".
 * Keeps the y-axis narrow so the plot area stays wide, while remaining
 * readable at a glance.
 */
function compactNumber(value) {
 if (value == null || value ==='') return'';
 const n = Number(value);
 if (!isFinite(n)) return'';
 const abs = Math.abs(n);
 if (abs >= 1e9) return`${(n / 1e9).toFixed(abs % 1e9 === 0 ? 0 : 1)}B`;
 if (abs >= 1e6) return`${(n / 1e6).toFixed(abs % 1e6 === 0 ? 0 : 1)}M`;
 if (abs >= 1e3) return`${(n / 1e3).toFixed(abs % 1e3 === 0 ? 0 : 1)}k`;
 return String(n);
}

/**
 * True when the viewport is narrow enough that in-SVG axis captions would
 * crowd the plot area. On those screens we drop the SVG labels and show the
 * same information as an HTML caption under the title instead, where it can
 * wrap instead of colliding.
 */
function useIsNarrow(query ='(max-width: 767px)') {
 const [isNarrow, setIsNarrow] = useState(
 () => typeof window !=='undefined' && window.matchMedia
 ? window.matchMedia(query).matches
 : false
 );

 useEffect(() => {
 if (typeof window ==='undefined' || !window.matchMedia) return undefined;
 const mql = window.matchMedia(query);
 const onChange = (e) => setIsNarrow(e.matches);
 setIsNarrow(mql.matches);
 // Safari <14 only supports the deprecated addListener API.
 if (mql.addEventListener) mql.addEventListener('change', onChange);
 else mql.addListener(onChange);
 return () => {
 if (mql.removeEventListener) mql.removeEventListener('change', onChange);
 else mql.removeListener(onChange);
 };
 }, [query]);

 return isNarrow;
}

export default function AreaChartComponent({
 data,
 dataKeys,
 xAxisKey ='timestamp',
 title,
 height = 300,
 stacked = false,
 formatXAxis,
 formatTooltip,
 exportFilename,
 // Axis labelling. Without these the reader has to infer the unit from the
 // chart title, which is guesswork for anyone who didn't build the page.
 xAxisLabel ='Time',
 yAxisLabel,
 // Tick formatter for the value axis. Defaults to compact counts; pass a
 // custom one for units like milliseconds.
 formatYAxis,
 // Friendly series names, keyed by dataKey. Raw keys come straight from the
 // KQL projection ("AvgLatency", "P95Latency", "PromptTokens") which reads
 // as jargon to a business audience.
 seriesLabels,
}) {
 const xFormatter = formatXAxis || ((val) => {
 if (!val) return'';
 const d = new Date(val);
 return d.toLocaleDateString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
 });

 const yFormatter = formatYAxis || compactNumber;
 const legendFormatter = (value) => seriesLabels?.[value] || value;
 const isNarrow = useIsNarrow();

 // On narrow screens the in-SVG axis captions steal too much of an already
 // small plot area, so we render them as a wrapping HTML caption instead.
 const showSvgAxisLabels = !isNarrow;
 const captionParts = [
 yAxisLabel ? `Y: ${yAxisLabel}` : null,
 xAxisLabel ? `X: ${xAxisLabel}` : null,
 ].filter(Boolean);

 return (
 <div className="chart-container">
 <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
 {title && <h3 className="text-sm font-semibold text-gray-700">{title}</h3>}
 {exportFilename && <ExportButton data={data} filename={exportFilename} />}
 </div>
 {/* Axis caption for narrow screens, where SVG labels would crowd the
 plot. Hidden on md+ because the in-chart labels take over there. */}
 {isNarrow && captionParts.length > 0 && (
 <p className="text-[11px] text-gray-500 mb-2 leading-snug">
 {captionParts.join(' · ')}
 </p>
 )}
 <ResponsiveContainer width="100%" height={height}>
 {/*
 Margins are responsive:
 - bottom leaves room for the x-axis caption ONLY when it's rendered
 inside the SVG; otherwise the tick labels alone need very little.
 - left leaves room for the rotated y-axis caption.
 The Legend is anchored to the TOP so it can never collide with the
 x-axis label sitting at the bottom — that overlap was the bug.
 */}
 <RechartsAreaChart
 data={data}
 margin={{
 top: 5,
 right: 8,
 bottom: showSvgAxisLabels && xAxisLabel ? 28 : 4,
 left: showSvgAxisLabels && yAxisLabel ? 8 : 0,
 }}
 >
 <defs>
 {dataKeys.map((key, idx) => (
 <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
 <stop offset="5%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={0.3} />
 <stop offset="95%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={0} />
 </linearGradient>
 ))}
 </defs>
 <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" />
 <XAxis
 dataKey={xAxisKey}
 tickFormatter={xFormatter}
 tick={{ fontSize: isNarrow ? 10 : 11 }}
 className="text-gray-500"
 minTickGap={isNarrow ? 24 : 12}
 label={showSvgAxisLabels && xAxisLabel ? {
 value: xAxisLabel,
 position:'insideBottom',
 offset: -18,
 style: { fontSize: 11, fill:'#6b7280' },
 } : undefined}
 />
 <YAxis
 tick={{ fontSize: isNarrow ? 10 : 11 }}
 tickFormatter={yFormatter}
 className="text-gray-500"
 width={isNarrow ? 38 : 52}
 label={showSvgAxisLabels && yAxisLabel ? {
 value: yAxisLabel,
 angle: -90,
 position:'insideLeft',
 style: { fontSize: 11, fill:'#6b7280', textAnchor:'middle' },
 } : undefined}
 />
 <Tooltip
 contentStyle={{
 backgroundColor:'var(--tooltip-bg, #fff)',
 color:'var(--tooltip-fg, #111827)',
 border:'1px solid var(--tooltip-border, #e5e7eb)',
 borderRadius:'8px',
 fontSize:'12px',
 }}
 labelStyle={{ color:'var(--tooltip-fg, #111827)' }}
 itemStyle={{ color:'var(--tooltip-fg, #111827)' }}
 formatter={formatTooltip}
 />
 <Legend
 iconSize={9}
 verticalAlign="top"
 align="right"
 height={24}
 wrapperStyle={{ fontSize: isNarrow ?'11px' :'12px', paddingBottom: 4 }}
 formatter={legendFormatter}
 />
 {dataKeys.map((key, idx) => (
 <Area
 key={key}
 type="monotone"
 dataKey={key}
 name={seriesLabels?.[key] || key}
 stroke={COLORS[idx % COLORS.length]}
 fill={`url(#grad-${key})`}
 strokeWidth={2}
 stackId={stacked ?'stack' : undefined}
 dot={false}
 activeDot={{ r: 4 }}
 />
 ))}
 </RechartsAreaChart>
 </ResponsiveContainer>
 </div>
 );
}
